import * as THREE from 'three';
import { settings } from '@/config';
import { TOTAL_FRAMES } from './SceneSections';
import { toVector3, smoothingFactor } from '@/utils/math';
import type { CameraController } from '@/camera/CameraController';
import type { ScrollManager } from '@/scroll/ScrollManager';
import type { TickState } from '@/types';

/** A baked GLB camera that gets scrubbed by scroll progress (production pipeline). */
export interface BakedCameraSource {
  /** The animated root whose world matrices are refreshed before sampling. */
  root: THREE.Object3D;
  camera: THREE.Camera;
  mixer: THREE.AnimationMixer;
  duration: number;
  /** When true, `settings.camera.focalLength` has already been baked into the
   *  CameraController's FOV once — don't overwrite it every frame from the GLB's
   *  own (possibly different) baked FOV. */
  lockFov?: boolean;
}

/**
 * The single technical animation permitted in Phase 01: maps scroll progress to
 * camera motion to verify the scroll → camera → render pipeline end to end.
 *
 *  - Preferred path: scrub a baked GLB camera (`mixer.setTime(progress*duration)`),
 *    sample its world transform, and feed the CameraController — exactly the
 *    production mechanism (one continuous camera path across four scenes).
 *  - Fallback path: interpolate a Catmull-Rom spline through placeholder waypoints
 *    so the pipeline is testable even before a baked camera exists.
 *
 * No UI, no content — purely a rendering-pipeline verification.
 */
export class ScrollCameraTest {
  enabled = settings.camera.scrollTest.enabled;

  private readonly positionCurve: THREE.CatmullRomCurve3;
  private readonly targetCurve: THREE.CatmullRomCurve3;
  private baked: BakedCameraSource | null = null;
  /** Eased scrub progress (lags behind the real scroll progress) — `null` until the
   *  first update, so activating this system doesn't sweep in from 0. */
  private smoothedProgress: number | null = null;
  /** Absolute clip fraction (0-1) that scroll progress 0 maps to. The intro fly-in plays
   *  frames 0→N automatically, then hands to scroll which should resume from frame N (not
   *  rewind to 0) — so scroll progress 0..1 maps to clip fraction [introStartFraction, 1]. */
  introStartFraction = 0;
  /** The clip fraction most recently pushed to the camera (set by {@link sampleAt}) — the
   *  authoritative "current frame" for frame-driven systems like the CameraFog envelope. */
  lastSampledFraction = 0;

  private readonly _pos = new THREE.Vector3();
  private readonly _target = new THREE.Vector3();
  private readonly _quat = new THREE.Quaternion();
  private readonly _scale = new THREE.Vector3();

  constructor(
    private readonly controller: CameraController,
    private readonly scroll: ScrollManager,
  ) {
    const waypoints = settings.camera.scrollTest.waypoints;
    this.positionCurve = new THREE.CatmullRomCurve3(waypoints.map((w) => toVector3(w.position)));
    this.targetCurve = new THREE.CatmullRomCurve3(waypoints.map((w) => toVector3(w.target)));
  }

  /** Switch to the baked-camera pipeline once a model provides a camera + clip. */
  useBakedCamera(source: BakedCameraSource): void {
    this.baked = source;
  }

  get usesBakedCamera(): boolean {
    return this.baked !== null;
  }

  update(tick: TickState): void {
    if (!this.enabled) return;
    const targetProgress = THREE.MathUtils.clamp(this.scroll.state.progress, 0, 1);

    // Ease the SCRUB itself toward the real scroll progress — a GSAP-ScrollTrigger
    // "scrub: <number>"-style smoothed lag — instead of mapping scroll 1:1 straight
    // into `mixer.setTime()`, so the baked camera glides with a soft catch-up instead
    // of snapping to every wheel tick (2026-07-05, user: "smooth like gsap easing").
    if (this.smoothedProgress === null) {
      this.smoothedProgress = targetProgress; // first frame: snap, don't sweep from 0
    } else if (targetProgress < this.smoothedProgress - 0.5) {
      // ScrollManager's end-of-scroll LOOP just snapped raw progress from ~1 back to
      // 0 — follow it instantly instead of easing "backward" through the last
      // fraction of the clip, which would look like a brief rewind glitch.
      this.smoothedProgress = targetProgress;
    } else {
      const ease = smoothingFactor(settings.camera.scrollEase, tick.delta);
      let next = this.smoothedProgress + (targetProgress - this.smoothedProgress) * ease;
      // SPEED LIMIT (2026-09-01, user: "when I scroll fast the camera passes through some mesh
      // — I want it slow even if someone is scrolling so fast"): a GLOBAL cap on how many clip
      // frames one tick may advance, tightened further inside the slow zones (2026-07-23,
      // Chapter 3 "so fast on scroll"). A hard flick still arrives — the camera just travels
      // there at a composed pace, and can never skip through geometry. Both directions.
      const clipSpan = 1 - this.introStartFraction;
      if (clipSpan > 0) {
        const frame = (this.introStartFraction + this.smoothedProgress * clipSpan) * TOTAL_FRAMES;
        let cap = settings.camera.maxFramesPerSec > 0
          ? settings.camera.maxFramesPerSec
          : Number.POSITIVE_INFINITY;
        for (const zone of settings.camera.slowZones) {
          if (frame < zone.startFrame || frame > zone.endFrame) continue;
          cap = Math.min(cap, zone.maxFramesPerSec);
          break;
        }
        if (Number.isFinite(cap)) {
          const maxStep = (cap * tick.delta) / (TOTAL_FRAMES * clipSpan);
          const step = next - this.smoothedProgress;
          if (Math.abs(step) > maxStep) next = this.smoothedProgress + Math.sign(step) * maxStep;
        }
      }
      this.smoothedProgress = next;
    }
    const progress = this.smoothedProgress;

    if (this.baked) {
      // Map scroll progress into the post-intro clip window [introStartFraction, 1].
      const clipFraction = this.introStartFraction + progress * (1 - this.introStartFraction);
      this.sampleAt(clipFraction);
      return;
    }

    this.positionCurve.getPoint(progress, this._pos);
    this.targetCurve.getPoint(progress, this._target);
    this.controller.lookFrom(this._pos, this._target);
  }

  /** Scrub the baked camera to an ABSOLUTE clip fraction (0-1) and drive the controller —
   *  used by the {@link IntroSequence} auto-play before scroll takes over. No-op without a
   *  baked camera. Also seeds `smoothedProgress` so the first scroll frame doesn't jump. */
  sampleAt(fraction: number): void {
    if (!this.baked) return;
    const f = THREE.MathUtils.clamp(fraction, 0, 1);
    // Every camera path (intro, scroll, magnet glide, teleport) funnels through here, so this
    // is THE authoritative "where is the camera on the clip" — the CameraFog envelope reads it.
    this.lastSampledFraction = f;
    this.baked.mixer.setTime(f * this.baked.duration);
    this.baked.root.updateMatrixWorld(true);
    this.baked.camera.matrixWorld.decompose(this._pos, this._quat, this._scale);
    this.controller.targetPosition.copy(this._pos);
    this.controller.targetQuaternion.copy(this._quat);
    if (!this.baked.lockFov && (this.baked.camera as THREE.PerspectiveCamera).isPerspectiveCamera) {
      this.controller.targetFov = (this.baked.camera as THREE.PerspectiveCamera).fov;
    }
  }

  /** Reset the eased scrub so the next `update` snaps to current scroll instead of easing
   *  from a stale value (called when scroll takes over from the intro). */
  resetSmoothing(): void {
    this.smoothedProgress = null;
  }
}
